/**
 * MemoryDashboard — Admin-only observability dashboard for Sales AI memory system
 *
 * Displays:
 * - System health (pass/warn/fail)
 * - Extraction & search statistics
 * - Scheduler status
 * - Items needing review
 *
 * Access: Admin only, toggleable via Settings
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Brain,
  RefreshCw,
  Settings,
  AlertTriangle,
  Shield,
  Lock,
  Wrench,
  Loader2
} from 'lucide-react';
import { useRole } from '../../hooks/useRole';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import apiClient from '../../lib/apiClient';
import MemorySystemHealthCard from './MemorySystemHealthCard';
import MemoryStatsCard from './MemoryStatsCard';
import MemorySchedulerCard from './MemorySchedulerCard';
import MemoryReviewQueueCard from './MemoryReviewQueueCard';
import './MemoryDashboard.css';

function MemoryDashboard() {
  const { isAdmin } = useRole();
  const { preferences } = useUserPreferences();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const queryClient = useQueryClient();

  // Check if Memory Dashboard is enabled for this user
  const isMemoryDashboardEnabled = preferences?.memoryDashboardEnabled !== false;

  // Fetch all memory data
  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth
  } = useQuery({
    queryKey: ['memory', 'health'],
    queryFn: () => apiClient.get('/api/memory/health'),
    refetchInterval: 30000, // Poll every 30 seconds
    enabled: isAdmin && isMemoryDashboardEnabled,
  });

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats
  } = useQuery({
    queryKey: ['memory', 'stats'],
    queryFn: () => apiClient.get('/api/memory/stats'),
    refetchInterval: 60000, // Poll every minute
    enabled: isAdmin && isMemoryDashboardEnabled,
  });

  const {
    data: schedulerData,
    isLoading: schedulerLoading,
    error: schedulerError,
    refetch: refetchScheduler
  } = useQuery({
    queryKey: ['memory', 'scheduler', 'status'],
    queryFn: () => apiClient.get('/api/memory/scheduler/status'),
    refetchInterval: 30000,
    enabled: isAdmin && isMemoryDashboardEnabled,
  });

  const {
    data: reviewData,
    isLoading: reviewLoading,
    error: reviewError,
    refetch: refetchReview
  } = useQuery({
    queryKey: ['memory', 'digest', 'review-queue'],
    queryFn: () => apiClient.get('/api/memory/digest/review-queue'),
    refetchInterval: 60000,
    enabled: isAdmin && isMemoryDashboardEnabled,
  });

  // Maintenance mutation
  const maintenanceMutation = useMutation({
    mutationFn: () => apiClient.post('/api/memory/trigger-maintenance', {}),
    onSuccess: (data) => {
      setMaintenanceResult({ success: true, stats: data.stats });
      // Refresh all data after maintenance
      queryClient.invalidateQueries({ queryKey: ['memory'] });
      setTimeout(() => setMaintenanceResult(null), 5000);
    },
    onError: (err) => {
      setMaintenanceResult({ success: false, error: err.message });
      setTimeout(() => setMaintenanceResult(null), 5000);
    },
  });

  // Handle refresh all
  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchHealth(),
        refetchStats(),
        refetchScheduler(),
        refetchReview(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="md-access-denied">
        <div className="md-access-denied-content">
          <Lock size={48} />
          <h2>Admin Access Required</h2>
          <p>The Memory Dashboard is only available to administrators.</p>
        </div>
      </div>
    );
  }

  // Dashboard disabled via settings
  if (!isMemoryDashboardEnabled) {
    return (
      <div className="md-disabled">
        <div className="md-disabled-content">
          <Shield size={48} />
          <h2>Memory Dashboard Disabled</h2>
          <p>This dashboard has been disabled in your settings.</p>
          <p className="md-disabled-hint">
            Go to <Settings size={14} /> Settings → Memory Dashboard to enable it.
          </p>
        </div>
      </div>
    );
  }

  const lastRefresh = new Date().toLocaleTimeString();

  return (
    <div className="md-container">
      {/* Header */}
      <header className="md-header">
        <div className="md-header-left">
          <Brain size={28} className="md-header-icon" />
          <div>
            <h1 className="md-title">Memory Dashboard</h1>
            <p className="md-subtitle">Sales AI Memory System Observability</p>
          </div>
        </div>
        <div className="md-header-right">
          <span className="md-last-refresh">Last refresh: {lastRefresh}</span>
          <button
            className="md-maintenance-btn"
            onClick={() => maintenanceMutation.mutate()}
            disabled={maintenanceMutation.isPending}
            title="Run maintenance tasks (decay, health scoring, embedding backfill)"
          >
            {maintenanceMutation.isPending ? (
              <Loader2 size={16} className="md-spin" />
            ) : (
              <Wrench size={16} />
            )}
            Run Maintenance
          </button>
          <button
            className="md-refresh-btn"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
          >
            <RefreshCw size={16} className={isRefreshing ? 'md-spin' : ''} />
            Refresh All
          </button>
        </div>
      </header>

      {/* Maintenance Result Banner */}
      {maintenanceResult && (
        <div className={`md-maintenance-result ${maintenanceResult.success ? 'md-maintenance-result--success' : 'md-maintenance-result--error'}`}>
          {maintenanceResult.success ? (
            <>
              <span>Maintenance completed:</span>
              <span>Decay: {maintenanceResult.stats?.decayUpdates || 0}</span>
              <span>Health: {maintenanceResult.stats?.healthUpdates || 0}</span>
              <span>Embeddings: {maintenanceResult.stats?.embeddingBackfill?.updated || 0}</span>
            </>
          ) : (
            <span>Maintenance failed: {maintenanceResult.error}</span>
          )}
        </div>
      )}

      {/* Health Overview Banner */}
      {healthData && (
        <div className={`md-health-banner md-health-banner--${healthData.status}`}>
          <span className="md-health-status">
            System Status: <strong>{healthData.status?.toUpperCase()}</strong>
          </span>
          {healthData.issues?.length > 0 && (
            <span className="md-health-issues">
              <AlertTriangle size={14} />
              {healthData.issues.length} issue{healthData.issues.length !== 1 ? 's' : ''} detected
            </span>
          )}
        </div>
      )}

      {/* Main Grid */}
      <div className="md-grid">
        <MemorySystemHealthCard
          data={healthData}
          isLoading={healthLoading}
          error={healthError}
          onRefresh={refetchHealth}
        />

        <MemoryStatsCard
          data={statsData}
          isLoading={statsLoading}
          error={statsError}
          onRefresh={refetchStats}
        />

        <MemorySchedulerCard
          data={schedulerData}
          isLoading={schedulerLoading}
          error={schedulerError}
          onRefresh={refetchScheduler}
        />

        <MemoryReviewQueueCard
          data={reviewData}
          isLoading={reviewLoading}
          error={reviewError}
          onRefresh={refetchReview}
        />
      </div>

      {/* Admin Notice */}
      <footer className="md-footer">
        <Shield size={14} />
        <span>Admin-only dashboard. Toggle visibility in Settings → Memory Dashboard.</span>
      </footer>
    </div>
  );
}

export default MemoryDashboard;
